import { RefObject, useCallback, useRef, useState } from 'react';

import type { Column, IRowNode } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { addDays, format } from 'date-fns';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { IconField } from 'primereact/iconfield';
import { InputIcon } from 'primereact/inputicon';
import { InputText } from 'primereact/inputtext';
import { Menu } from 'primereact/menu';
import { MenuItem } from 'primereact/menuitem';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';

import { useAppSelector } from '@/app/hooks';
import { useGetPredictionTechsQuery } from '@/app/services/equipment';
import {
  FreshShipmentActions,
  FreshShipmentType,
} from '@/app/slices/freshShipment';
import { addToast } from '@/app/slices/toast';
import { RootState } from '@/app/store';
import { ToastLife } from '@/components/hoc/toast-container/enums';
import usePageTitle from '@/components/hooks/usePageTitle';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import {
  FertilizationQCResponse,
  TOA_GetFreshShipmentFertList,
} from '@/rtk-query.gql';
import { useCreateClientEmbryosMutation } from '@/services/embryo-service';
import {
  useCreateCommDairyExcel,
  useFreshShipmentCacheInvalidation,
  useLazyFertilizationList,
  useMatingLabels,
  useMatingLabelsDupes,
  useSendCommDairyEmail,
  useShipmentGroupMastersQuery,
  useSignOff,
  useUpdateFertTransaction,
  useUpdateIncubatorAssignment,
} from '@/services/fresh-shipment/hooks';
import { useSiteByIdQuery } from '@/services/sites';
import { sanitizeForUrl } from '@/utils';
import { setLocalStorage } from '@/utils/storage';

import {
  allowedExportColumns,
  allowedExportColumnsWithPTA,
} from '../utils/exportColumns';
import {
  getExportColumnOrder,
  getExportColumnOrderWithPTA,
} from '../utils/exportUtils';
import { convertToCSV, downloadCSV, generateExcelFile } from '../utils/utils';
import { SelectedFert } from './BillableShippedDetailGrid/index';
import { FreshShipmentQCInformationDialog } from './FreshShipmentQCInformationDialog';
import { UploadTransferPregCheck } from './UploadTransferPregCheck';

import './FreshShipmentToolbar.css';

type FreshShipmentActionsProps = {
  className?: string;
  quickSearchValue: string;
  setQuickSearchValue: (value: string) => void;
  selectedShipmentGroups: { id: string; value: string }[];
  signedOffShipmentGroupIds: string[];
  fertQcData: FertilizationQCResponse;
  checkQcLoaded(fertQc: FertilizationQCResponse): void;
  fertQc: FertilizationQCResponse;
  ivfGridRef: React.RefObject<AgGridReact>;
  csrGridRef: React.RefObject<AgGridReact>;
  signedOffGridRef: React.RefObject<AgGridReact>;
  freshShipmentGridRef: React.RefObject<AgGridReact>;
  selectedFertRef: RefObject<SelectedFert[]>;
};

export const FreshShipmentToolbar = ({
  fertQcData,
  quickSearchValue,
  setQuickSearchValue,
  selectedShipmentGroups,
  signedOffShipmentGroupIds,
  checkQcLoaded,
  fertQc,
  ivfGridRef,
  csrGridRef,
  signedOffGridRef,
  freshShipmentGridRef: _freshShipmentGridRef,
  selectedFertRef,
}: FreshShipmentActionsProps) => {
  const isMultiSelectFertEnabled = useFeatureFlag(
    'Select_Multiple_Ferts_Export_Embryo',
  );
  const isClientTransferredFixEnabled = useFeatureFlag('fs_client_transferred');
  const isProgramNameExportEnabled = useFeatureFlag(
    'Fresh_Shipment_Export_Program_Name',
  );
  const { t } = useTranslation();
  const { setTitle } = usePageTitle();
  const dispatch = useDispatch();
  const { me } = useAppSelector((state) => state.authentication);
  const { qcLoaded } = useSelector((state: RootState) => state.freshShipment);
  const { selectedRows, showForm } = useSelector(
    (state: { freshShipment: FreshShipmentType }) => state.freshShipment,
  );
  const exportMenuRef = useRef<Menu>(null);
  const copyMenuRef = useRef<Menu>(null);
  const actionsMenuRef = useRef<Menu>(null);

  const [params, setParams] = useSearchParams();
  const groupId = params.get('group-id');
  const date = params.get('date');
  const siteId = params.get('sId');
  const d5 = params.get('d5');
  const isCsr = params.get('isCsr') === 'true';
  const signedOff = signedOffShipmentGroupIds?.includes(groupId as string);
  const isPredictionTechDropdownEnabled = useFeatureFlag(
    'Prediction_Tech_Dropdown_Fresh_Shipment',
  );

  const [predictionTechState, setPredictionTechState] = useState<string | null>(
    null,
  );
  const selectedPredictionTech = isPredictionTechDropdownEnabled
    ? params.get('predTech')
    : predictionTechState;
  const [selectedIncubator, setSelectedIncubator] = useState<string | null>(
    null,
  );
  const [qcDialogVisible, setQcDialogVisible] = useState(false);

  const [isNormalLabelsLoading, setIsNormalLabelsLoading] = useState(false);
  const [isDuplicateLabelsLoading, setIsDuplicateLabelsLoading] =
    useState(false);
  const [isEmbryoCsvLoading, setIsEmbryoCsvLoading] = useState(false);
  const [uploadDialogVisible, setUploadDialogVisible] = useState(false);

  const { data: site } = useSiteByIdQuery(siteId ?? undefined);
  const siteName = site?.name;

  const invalidateCache = useFreshShipmentCacheInvalidation();
  const getFertilizations = useLazyFertilizationList();
  const { mutateAsync: createClientEmbryos } = useCreateClientEmbryosMutation();

  const { data: incubatorData } = useShipmentGroupMastersQuery({
    fertilizationSiteId: siteId,
    shipGroupId: groupId,
  });
  const { data: predictionTechsList } = useGetPredictionTechsQuery(
    {
      where: {
        or: [{ farmlocation: { eq: false } }, { farmlocation: { eq: null } }],
      },
    },
    { skip: !groupId || !siteId },
  );

  const incubatorList = incubatorData?.shipmentGroupMasters;

  const { mutateAsync: createCommDairyExcel } = useCreateCommDairyExcel();
  const { mutateAsync: sendCommDairyFertilizationEmail } =
    useSendCommDairyEmail();

  const { mutateAsync: updateFertTransaction } = useUpdateFertTransaction();
  const { mutateAsync: updateMatingLabels } = useMatingLabels();
  const { mutateAsync: updateMatingLabelsDupes } = useMatingLabelsDupes();
  const { mutateAsync: updateIncubatorAssignment } =
    useUpdateIncubatorAssignment();
  const { mutateAsync: signOff, isPending: signOffLoading } = useSignOff();

  const handleExportCSV = async () => {
    const ivfGridApi = ivfGridRef.current?.api;
    const csrGridApi = csrGridRef.current?.api;
    const signedOffGridApi = signedOffGridRef.current?.api;
    const activeGridApi = isCsr ? csrGridApi : signedOffGridApi || ivfGridApi;

    const columnState = activeGridApi?.getAllGridColumns();

    const finalColumnIds = getExportColumnOrder(
      columnState as Column<any>[],
      allowedExportColumns,
    );

    const rows: TOA_GetFreshShipmentFertList[] = [];
    activeGridApi?.forEachNodeAfterFilterAndSort((node: IRowNode<any>) => {
      if (node.data) rows.push(node.data);
    });

    const csvData = rows.map((row) => {
      const obj: Record<string, string> = {};
      for (const colId of finalColumnIds) {
        const header = allowedExportColumns[colId];
        let value = '';

        const raw = row[colId as keyof TOA_GetFreshShipmentFertList];
        value = raw != null ? raw.toString() : '';
        obj[header] = value;
      }
      return obj;
    });

    const csvContent = convertToCSV(csvData);
    const prefix = signedOffGridApi ? 'SignedOff_' : isCsr ? 'CSR_' : 'IVF_';
    downloadCSV(csvContent, `${prefix}FreshShipment_${date}.csv`);
  };

  const handleExportCSVWithPTA = async () => {
    const ivfGridApi = ivfGridRef.current?.api;
    const csrGridApi = csrGridRef.current?.api;
    const signedOffGridApi = signedOffGridRef.current?.api;
    const activeGridApi = isCsr ? csrGridApi : signedOffGridApi || ivfGridApi;

    const columnState = activeGridApi?.getAllGridColumns();

    const finalColumnIds = getExportColumnOrderWithPTA(
      columnState as Column<any>[],
      allowedExportColumnsWithPTA,
    );

    const rows: TOA_GetFreshShipmentFertList[] = [];
    activeGridApi?.forEachNodeAfterFilterAndSort((node: IRowNode<any>) => {
      if (node.data) rows.push(node.data);
    });

    const csvData = rows.map((row) => {
      const obj: Record<string, string> = {};
      for (const colId of finalColumnIds) {
        const header = allowedExportColumnsWithPTA[colId];
        const raw = row[colId as keyof TOA_GetFreshShipmentFertList];
        obj[header] = raw != null ? raw.toString() : '';
      }
      return obj;
    });

    const csvContent = convertToCSV(csvData);
    const prefix = signedOffGridApi ? 'SignedOff_' : isCsr ? 'CSR_' : 'IVF_';
    downloadCSV(csvContent, `${prefix}FreshShipmentPTA_${date}.csv`);
  };

  const handleExportCommercialDairy = async () => {
    if (!date || !siteId || !groupId) {
      return;
    }

    try {
      const blob = await createCommDairyExcel({
        fertilizationDate: date as string,
        fertilizationSiteId: siteId as string,
        shipGroupId: groupId as string,
        d5: d5 === 'true',
      });

      const excelUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = excelUrl;
      link.download = 'PredictionData.xlsx';
      link.click();
      setTimeout(() => URL.revokeObjectURL(excelUrl), 100);
    } catch (error) {
      throw new Error(`Error creating mating labels: ${error}`, {
        cause: error,
      });
    }
  };

  const exportItems: MenuItem[] = [
    {
      label: t('export.exportCsv'),
      icon: 'pi pi-file-excel',
      command: handleExportCSV,
    },
    {
      label: t('export.exportCsvWithPta'),
      icon: 'pi pi-file-excel',
      command: handleExportCSVWithPTA,
    },
    {
      label: t('export.exportCommercialDairy'),
      icon: 'pi pi-file-excel',
      command: handleExportCommercialDairy,
    },
  ];

  const handleCopyShipHeld = async (shippedTog: boolean = false) => {
    const data = await getFertilizations({
      fertilizationDate: date as string,
      fertilizationSiteId: siteId as string,
      shipGroupId: groupId as string,
    });

    for (const rowId of selectedRows) {
      const fertilization = data?.find(
        (fert: TOA_GetFreshShipmentFertList) => fert.fertilizationId === rowId,
      );
      if (fertilization) {
        const ivfshipped = fertilization.origShipped?.toString() || '';
        const ivfheld = fertilization.origHeldAtTog?.toString() || '';

        if (shippedTog) {
          await updateFertTransaction({
            id: rowId,
            field: 'shippedTog',
            value: ivfshipped,
            updatePredictedComments: false,
          });

          await updateFertTransaction({
            id: rowId,
            field: 'shipped',
            value: '',
            updatePredictedComments: false,
          });
        } else {
          await updateFertTransaction({
            id: rowId,
            field: 'shipped',
            value: ivfshipped,
            updatePredictedComments: false,
          });

          await updateFertTransaction({
            id: rowId,
            field: 'shippedTog',
            value: '',
            updatePredictedComments: false,
          });
        }

        await updateFertTransaction({
          id: rowId,
          field: 'heldAtTogToFreeze',
          value: ivfheld,
          updatePredictedComments: false,
        });
        invalidateCache(['FertilizationListFreshShipment']);
      }
    }
  };

  const copyItems: MenuItem[] = [
    {
      label: t('freshShipment.copyShipHeld'),
      icon: 'pi pi-copy',
      command: () => handleCopyShipHeld(false),
    },
    {
      label: t('freshShipment.copyShipHeldTog'),
      icon: 'pi pi-copy',
      command: () => handleCopyShipHeld(true),
    },
  ];

  const handleUpdatePredictionTech = async (clear: boolean = false) => {
    dispatch({
      type: FreshShipmentActions.UPDATE_PREDICTION_TECH_LOADING,
      predictionTechLoading: true,
    });
    const value = clear ? '' : (selectedPredictionTech as string);
    if (clear && isPredictionTechDropdownEnabled) {
      setParams((prev) => {
        const next = new URLSearchParams(prev);
        next.delete('predTech');
        return next;
      });
    }
    await Promise.all(
      selectedRows.map((row) =>
        updateFertTransaction({
          id: row,
          field: 'predictedById',
          value,
          updatePredictedComments: false,
        }),
      ),
    );
    invalidateCache([
      'FertilizationListFreshShipment',
      'SplitFertilizationList',
      'CurrentFertPredictionTech',
    ]);
    const storedRows = JSON.parse(
      localStorage.getItem('freshShipment') || '{}',
    );
    const updatedRows = (storedRows.selectedFreshShipmentRows || []).filter(
      (row: string) => !selectedRows.includes(row),
    );
    setLocalStorage('freshShipment', updatedRows, 'selectedFreshShipmentRows');
    dispatch({
      type: FreshShipmentActions.UPDATE_PREDICTION_TECH_LOADING,
      predictionTechLoading: false,
    });
  };

  const handleDuplicateWindow = () => {
    for (const item of selectedShipmentGroups) {
      const currentParams = new URLSearchParams(params);
      const formattedName = sanitizeForUrl(item.value || '');

      currentParams.set('group-id', item.id as string);

      const queryString = currentParams.toString();

      const url = `/fresh-shipment/${formattedName}?${queryString}`;

      const newWindow = window.open(url, `_blank_${item.id as string}`);

      if (newWindow) {
        newWindow.document.title = `${item.value}`;
      }
    }
  };

  const handleMatingLabels = async () => {
    setIsNormalLabelsLoading(true);
    try {
      const blob = await updateMatingLabels({
        fertilizationDate: date as string,
        site: (() => {
          const labelSiteName = siteName || '';
          return labelSiteName.includes(' - ')
            ? labelSiteName.split(' - ')[0]
            : labelSiteName;
        })(),
        shipGroups: selectedShipmentGroups
          .map((group) => group.value)
          .join('|'),
      });

      const excelUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = excelUrl;
      link.download = 'Cryo Labels For Shipping - Fresh Shipment.xlsx';
      link.click();
      setTimeout(() => URL.revokeObjectURL(excelUrl), 100);
    } catch (error) {
      throw new Error(`Error creating mating labels: ${error}`, {
        cause: error,
      });
    } finally {
      setIsNormalLabelsLoading(false);
    }
  };

  const handleMatingLabelsDupes = async () => {
    setIsDuplicateLabelsLoading(true);
    try {
      const blob = await updateMatingLabelsDupes({
        fertilizationDate: date as string,
        site: (() => {
          const labelSiteName = siteName || '';
          return labelSiteName.includes(' - ')
            ? labelSiteName.split(' - ')[0]
            : labelSiteName;
        })(),
        shipGroups: selectedShipmentGroups
          .map((group) => group.value)
          .join('|'),
      });

      const excelUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = excelUrl;
      link.download = 'Cryo Labels For Shipping - Fresh Shipment Dupes.xlsx';
      link.click();
      setTimeout(() => URL.revokeObjectURL(excelUrl), 100);
    } catch (error) {
      throw new Error(`Error creating mating labels: ${error}`, {
        cause: error,
      });
    } finally {
      setIsDuplicateLabelsLoading(false);
    }
  };

  const handleUpdateIncubator = async () => {
    await Promise.all(
      selectedRows.map((row) =>
        updateIncubatorAssignment({
          fertilizationId: row,
          value: selectedIncubator as string,
        }),
      ),
    );
    invalidateCache([
      'FertilizationListFreshShipment',
      'SplitFertilizationList',
      'CurrentFertPredictionTech',
    ]);
  };
  const handleBackToShipGroups = useCallback(() => {
    setTitle(t('freshShipment.freshShipment'));
    document.title = t('freshShipment.freshShipment');
    const newParams = new URLSearchParams(params);
    newParams.delete('group-id');
    newParams.delete('title');
    setParams(newParams);
  }, [checkQcLoaded, fertQc, params, setParams, setTitle, t]);

  const handleSignOff = async () => {
    if (!date || !siteId || !groupId) {
      return;
    }

    const daysAfterFert = d5 === 'true' ? 5 : 6;
    const fertDate = new Date(date);
    const earliestSignOff = new Date(fertDate);
    earliestSignOff.setDate(earliestSignOff.getDate() + daysAfterFert);

    if (new Date() < earliestSignOff) {
      dispatch(
        addToast({
          severity: 'warn',
          summary: t('common.warning'),
          detail: `Sign off is not available until ${format(earliestSignOff, 'MM/dd/yyyy')}. Please wait until day ${daysAfterFert} after fertilization.`,
          sticky: true,
        }),
      );
      return;
    }

    try {
      const response = await signOff({
        username: me?.email?.split('@')[0],
        fertilizationDate: date,
        fertilizationSiteId: siteId,
        shipGroupId: groupId,
      });
      if (!response?.success) {
        throw new Error(response?.message);
      }

      handleBackToShipGroups();
      invalidateCache([
        'FertilizationListFreshShipment',
        'FreshShipmentList',
        'ShipGroupList',
      ]);

      dispatch(
        addToast({
          severity: 'success',
          summary: t('common.success'),
          detail: t('freshShipment.signOffSuccessful'),
          sticky: true,
        }),
      );
    } catch (e) {
      dispatch(
        addToast({
          severity: 'error',
          summary: t('common.error'),
          detail: t('freshShipment.errorSigningOff') + ' ' + e,
          sticky: true,
        }),
      );
    }
  };

  const handleUpdateQCData = () => {
    setQcDialogVisible(true);
  };

  const handleRefreshData = () => {
    invalidateCache([
      'FreshShipmentList',
      'QcFreshShipment',
      'ShipGroupList',
      'FertilizationListFreshShipment',
    ]);
  };

  const handleExportEmbryoCSV = async () => {
    if (!date || !siteId || !selectedFertRef.current?.length) {
      dispatch(
        addToast({
          severity: 'warn',
          summary: t('freshShipment.noFertSelected'),
          life: ToastLife.MEDIUM,
        }),
      );
      return;
    }

    setIsEmbryoCsvLoading(true);
    try {
      const headers = [
        'embryoid',
        'sirereg',
        'sirebreed',
        'donorreg',
        'donorbreed',
        'fertname',
        'strawno',
        'stage',
        'grade',
        'recip',
        'froze',
        'discard',
        'naab',
        'farmlocation',
        ...(isProgramNameExportEnabled ? ['programname'] : []),
      ];

      const columnWidths = [
        { wch: 12 },
        { wch: 15 },
        { wch: 15 },
        { wch: 15 },
        { wch: 15 },
        { wch: 20 },
        { wch: 12 },
        { wch: 10 },
        { wch: 10 },
        { wch: 15 },
        { wch: 8 },
        { wch: 8 },
        { wch: 10 },
        { wch: 20 },
        ...(isProgramNameExportEnabled ? [{ wch: 20 }] : []),
      ];

      const allData: string[][] = [];

      const selectedFerts = selectedFertRef.current;

      for (const selected of selectedFerts) {
        const { row, freshShipmentId } = selected;

        const transferDate = format(
          addDays(new Date(date as string), d5 === 'true' ? 5 : 6),
          'MM/dd/yyyy',
        );

        const clientEmbryos = await createClientEmbryos({
          fertID: row.fertilizationId,
          fertName: row.mating ?? '',
          fertSiteID: siteId as string,
          fertDate: date as string,
          transferDate,
          transferSiteID: siteId as string,
          freshShipmentID: freshShipmentId,
          calfType: isClientTransferredFixEnabled
            ? '100000009' // Non-TOG client transfer
            : '1',
          loadDate: '0',
          freshShippedNum: String(row.shipClient ?? ''),
        });

        if (!clientEmbryos || clientEmbryos.length === 0) continue;

        const rows = clientEmbryos.map((embryo) => [
          embryo.id != null ? String(embryo.id) : '',
          row.sireRegistrationNumber ?? '',
          row.sireBreedName ?? '',
          row.donorRegistrationNumber ?? '',
          row.donorBreedName ?? '',
          embryo.fertilizationName ?? '',
          embryo.strawNumber != null ? String(embryo.strawNumber) : '',
          embryo.stage != null ? String(embryo.stage) : '',
          embryo.grade != null ? String(embryo.grade) : '',
          embryo.recip ?? '',
          embryo.froze === true ? 'x' : '',
          embryo.discard === true ? 'x' : '',
          row.naab ?? '',
          embryo.farmLocationName ?? '',
          ...(isProgramNameExportEnabled ? [row.programName ?? ''] : []),
        ]);

        allData.push(...rows);
      }

      if (allData.length === 0) return;

      const formattedDate = date
        ? format(new Date(date as string), 'MM-dd-yyyy')
        : date;

      generateExcelFile(
        headers,
        allData,
        `FreshEmbryos-${formattedDate}-TOG.xlsx`,
        columnWidths,
      );
    } finally {
      setIsEmbryoCsvLoading(false);
    }
  };

  const handleSendCommDairyEmail = async () => {
    if (!date || !siteId || !groupId) {
      return;
    }

    await sendCommDairyFertilizationEmail({
      fertilizationDate: date as string,
      fertilizationSiteId: siteId as string,
      shipGroupId: groupId as string,
      email: me?.email,
      username: me?.email?.split('@')[0],
      d5: d5 === 'true',
    });
  };

  const actionItems: MenuItem[] = [
    {
      label: t('freshShipment.sendCommercialDairyEmail'),
      icon: 'pi pi-envelope',
      command: handleSendCommDairyEmail,
    },
  ];

  return (
    <div className="flex flex-wrap gap-3">
      <Button
        icon="pi pi-refresh"
        onClick={handleRefreshData}
        tooltip={t('common.refreshData')}
      />

      <Button
        onClick={() => {
          dispatch({
            type: FreshShipmentActions.UPDATE_IS_CSR,
            isCsr: !isCsr,
          });
        }}
        severity={isCsr ? 'success' : 'secondary'}
        label={isCsr ? 'CSR' : 'IVF'}
      />

      {showForm && (
        <>
          <IconField iconPosition="left">
            <InputIcon className="pi pi-search"> </InputIcon>
            <InputText
              placeholder={t('common.search')}
              value={quickSearchValue}
              className="fresh-shipment__search-input"
              onChange={(e) => setQuickSearchValue(e.target.value)}
            />
          </IconField>
          <div className="fresh-shipment__search-container">
            <div className="p-inputgroup">
              <Dropdown
                value={selectedPredictionTech}
                options={predictionTechsList || []}
                optionLabel="name"
                optionValue="id"
                onChange={(e) => {
                  if (isPredictionTechDropdownEnabled) {
                    setParams((prev) => {
                      const next = new URLSearchParams(prev);
                      if (e.value) {
                        next.set('predTech', e.value);
                      } else {
                        next.delete('predTech');
                      }
                      return next;
                    });
                  } else {
                    setPredictionTechState(e.value);
                  }
                }}
                editable
                placeholder={t('tech.predictionTech')}
              />
              <Button
                icon="pi pi-times"
                onClick={() => handleUpdatePredictionTech(true)}
                disabled={selectedRows.length === 0}
                severity="secondary"
                tooltip={t('tech.clearPredictionTech')}
              />
              <Button
                icon="pi pi-save"
                onClick={() => handleUpdatePredictionTech(false)}
                disabled={selectedRows.length === 0}
                tooltip={t('tech.updatePredictionTech')}
              />
            </div>

            {isCsr && (
              <div className="p-inputgroup">
                <Dropdown
                  value={selectedIncubator}
                  options={incubatorList || []}
                  optionLabel="name"
                  optionValue="shipmentGroupMasterId"
                  onChange={(e) => setSelectedIncubator(e.value)}
                  editable
                  placeholder={t('freshShipment.incubator')}
                />
                <Button
                  icon="pi pi-save"
                  onClick={handleUpdateIncubator}
                  disabled={selectedRows.length === 0}
                  tooltip={t('freshShipment.assignIncubator')}
                />
              </div>
            )}
          </div>

          {isCsr && (
            <>
              <div className="p-buttonset">
                <Button
                  label={t('freshShipment.copyShipHeld')}
                  severity="secondary"
                  icon="pi pi-chevron-down"
                  onClick={(event) => copyMenuRef.current?.toggle(event)}
                />
                <Menu model={copyItems} popup ref={copyMenuRef} />
              </div>
            </>
          )}
          <div className="p-buttonset">
            <Button
              label={t('export.export')}
              severity="secondary"
              icon="pi pi-chevron-down"
              onClick={(event) => exportMenuRef.current?.toggle(event)}
            />
            <Menu model={exportItems} popup ref={exportMenuRef} />
          </div>
          <div className="p-buttonset">
            <Button
              label={t('common.email')}
              severity="secondary"
              icon="pi pi-chevron-down"
              onClick={(event) => actionsMenuRef.current?.toggle(event)}
            />
            <Menu model={actionItems} popup ref={actionsMenuRef} />
          </div>
          {isCsr && (
            <div>
              <Button
                label={t('freshShipment.signOff')}
                onClick={handleSignOff}
                severity="success"
                className="sign-off"
                loading={signOffLoading}
                disabled={signedOff}
              ></Button>
            </div>
          )}
        </>
      )}
      {!showForm && (
        <div className="flex flex-wrap gap-3">
          <Button
            icon="pi pi-clone"
            label={t('freshShipment.duplicateWindow')}
            onClick={qcLoaded ? () => handleDuplicateWindow() : () => {}}
            severity="success"
            tooltip={!qcLoaded ? t('qc.saveQcDataFirst') : undefined}
            disabled={!date || !siteId}
          />
          <Button
            icon="pi pi-eye"
            label={t('qc.qcInformation')}
            disabled={!date || !siteId}
            onClick={handleUpdateQCData}
          />
          <FreshShipmentQCInformationDialog
            fertQcData={fertQcData as FertilizationQCResponse}
            visible={qcDialogVisible}
            onHide={() => setQcDialogVisible(false)}
          />
          <Button
            label={t('labels.normalLabels')}
            onClick={handleMatingLabels}
            disabled={!(selectedShipmentGroups?.length > 0)}
            severity="secondary"
            loading={isNormalLabelsLoading}
          ></Button>
          <Button
            label={t('labels.duplicateLabels')}
            onClick={handleMatingLabelsDupes}
            disabled={!(selectedShipmentGroups?.length > 0)}
            severity="secondary"
            loading={isDuplicateLabelsLoading}
          ></Button>
          <Button
            icon="pi pi-file"
            label={t('freshShipment.exportEmbryoCsv')}
            onClick={handleExportEmbryoCSV}
            severity="secondary"
            loading={isEmbryoCsvLoading}
          />
          <Button
            icon="pi pi-upload"
            label="Upload"
            onClick={() => setUploadDialogVisible(true)}
            severity="secondary"
          />
          <UploadTransferPregCheck
            visible={uploadDialogVisible}
            setVisible={setUploadDialogVisible}
            siteId={siteId || undefined}
            transferDate={date || undefined}
          />
        </div>
      )}
    </div>
  );
};
